import { translateSelector } from '@ackee/jerome';
import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash-es';
import isAlpha from 'voca/is_alpha';
import latinise from 'voca/latinise';

import type { PasswordSecret } from '@/modules/api/codegen';
import { selectActiveVaultId } from '@/modules/vaults';

import { GROUP_OTHER, ListItemType, SecretType } from '../../constants';
import { SearchSecretAttributeType, type ListSecret, type Secret } from '../../types';
import { isValueMatchingSearchTerm } from '../../utilities';
import type { SecretsState, SortBy } from '../reducers';
import { selectSecretsEntities, selectSecretsNotes } from './entities';
import { selectActiveSecretsAttributesFilters, selectSecrets, selectSecretsNameFilter, selectViewFilterIds } from './secrets';

const selectSecretsList = createSelector([selectSecretsEntities], ({ list }) => list);

export const selectMappedSecrets = createSelector(selectSecretsList, ({ byIds, ids }) => ids.map(id => byIds[id]));

export const selectPasswordSecrets = createSelector(selectMappedSecrets, secrets =>
    secrets.filter(secret => secret.type === SecretType.Password),
);

export const selectViewFilteredSecrets = createSelector([selectMappedSecrets, selectViewFilterIds], (secrets, viewIds) => {
    if (viewIds === null) {
        return secrets;
    }

    return secrets.filter(({ id }) => viewIds.includes(id));
});

export const selectSearchableSecrets = createSelector([selectViewFilteredSecrets, selectSecretsNotes], (secrets, notes) =>
    secrets.map(secret => ({ ...secret, note: notes[secret.id] })),
);

const selectSortedSecrets = createSelector(selectViewFilteredSecrets, secrets =>
    secrets
        .map<ListSecret>(secret => {
            const latinisedName = latinise(secret.name);
            let group = latinisedName.toLowerCase().charAt(0);
            if (!isAlpha(group)) {
                group = GROUP_OTHER;
            }
            return {
                ...secret,
                group,
                latinisedName,
                listItemType: ListItemType.SECRET,
                key: secret.id,
            };
        })
        .sort(function (secretA, secretB) {
            const nameA = secretA.latinisedName.toLowerCase();
            const nameB = secretB.latinisedName.toLowerCase();
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        }),
);

const selectSecretsFilteredByAttributes = createSelector(
    selectSortedSecrets,
    selectActiveSecretsAttributesFilters,
    selectActiveVaultId,
    (secrets, activeAttributesFilters, activeVaultId) => {
        const secretsInVault = activeVaultId ? secrets.filter(secret => secret.vaultId === activeVaultId) : secrets;

        if (!activeAttributesFilters.length) {
            return secretsInVault;
        }

        return secretsInVault.filter(secret =>
            activeAttributesFilters.every(filter => {
                switch (filter.type) {
                    case SearchSecretAttributeType.TAG:
                        return secret.tags?.includes(filter.value);
                    case SearchSecretAttributeType.GROUP:
                        return secret.groups?.some(group => group.id === filter.value);
                    default:
                        return false;
                }
            }),
        );
    },
);

function createPasskeyFilter(hasPasskey: boolean) {
    return function filterPasskey(secret: PasswordSecret) {
        const passkeyId = secret.passkey?.credentialId ?? null;

        return Boolean(passkeyId) === hasPasskey;
    };
}

function createSecretTypeFilter(type: SecretsState['secretType'], optional: boolean = false) {
    if (!type) {
        return () => optional;
    }

    const secretHasPasskeyFilter = createPasskeyFilter(true);

    return function filterSecretType(secret: ListSecret) {
        return type === 'passkey' ? secretHasPasskeyFilter(secret as PasswordSecret) : secret.type === type;
    };
}

function createStringComparator<T extends Record<string, any>>(order: SortBy['order'], field: keyof T) {
    return function stringComparato(a: T, b: T) {
        switch (order) {
            case 'asc':
                return a[field] && b[field] ? a[field].localeCompare(b[field]) : 0;
            case 'desc':
                return a[field] && b[field] ? b[field].localeCompare(a[field]) : 0;
            default:
                return 0;
        }
    };
}

function createDateComparator<T extends Record<string, any>>(order: SortBy['order'], field: keyof T) {
    return function dateComparator(a: T, b: T) {
        switch (order) {
            case 'asc':
                return new Date(a[field]).getTime() - new Date(b[field]).getTime();
            case 'desc':
                return new Date(b[field]).getTime() - new Date(a[field]).getTime();
            default:
                return 0;
        }
    };
}

function createComparator<T extends Record<string, any>>(order: SortBy['order'], field: keyof T) {
    return function numberComparator(a: T, b: T) {
        const aValue = a[field];
        const bValue = b[field];

        switch (order) {
            case 'asc':
                return aValue > bValue ? -1 : 1;

            case 'desc':
                return bValue > aValue ? 1 : -1;
            default:
                return 0;
        }
    };
}

function createSortByField<T extends Record<string, any>>({ field, order, comparator }: SortBy): (a: T, b: T) => number {
    switch (comparator) {
        case 'string':
            return createStringComparator(order, field);
        case 'date':
            return createDateComparator(order, field);
        default:
            return createComparator(order, field);
    }
}

function createSearchFilter(search: string, locale: string, notes: Record<Secret['id'], string>) {
    return function searchFilter(secret: ListSecret) {
        const note = notes[secret.id];

        return (
            isValueMatchingSearchTerm(secret.name, search, locale) ||
            ('username' in secret && secret.username && isValueMatchingSearchTerm(secret.username, search, locale)) ||
            (note && isValueMatchingSearchTerm(note, search, locale))
        );
    };
}

export const selectFilteredSecrets = createSelector(
    [selectSecretsFilteredByAttributes, selectSecretsNameFilter, selectSecrets, translateSelector, selectSecretsNotes],
    (secrets, search, { secretType, sortBy }, { locale }, notes) => {
        const secretTypeFilter = createSecretTypeFilter(secretType, true);

        const sortByField = createSortByField<ListSecret>(sortBy);
        const searchFilter = createSearchFilter(search, locale, notes);

        return secrets.filter(secret => secretTypeFilter(secret) && searchFilter(secret)).toSorted(sortByField);
    },
);

export const selectFilteredSecretsCount = createSelector(
    [selectFilteredSecrets, selectViewFilteredSecrets],
    (filteredSecrets, viewSecrets) => (filteredSecrets.length < viewSecrets.length ? filteredSecrets.length : null),
);

export const selectHasSecrets = createSelector([selectFilteredSecrets], secrets => !isEmpty(secrets));
